import { Box, Typography } from "@mui/material";
import { AxiosResponse } from "axios";
import React, { memo } from "react";
import ProgressIndicator from "../../progressIndicator/ProgressIndicator";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

interface ShowResultProps {
  isLoading: boolean;
  updateResult?: AxiosResponse;
}

const classes = {
  iconSuccess: {
    color: "var(--dmg-success-light)",
    marginBottom: 10,
  },
  iconFailed: {
    color: "var(--dmg-error-light)",
    marginBottom: 10,
  },
};

// helpper functions
const isSuccessResponse = (response: AxiosResponse) => {
  return response.hasOwnProperty("data") && !response.data.errors;
};

const ShowResult = (props: ShowResultProps) => {
  const { isLoading, updateResult } = props;

  // result components
  const SuccessIcon = (
    <CheckCircleOutlineIcon fontSize="large" sx={classes.iconSuccess} />
  );

  const FailIcon = (
    <HighlightOffIcon fontSize="large" sx={classes.iconFailed} />
  );

  const FailMsg = (
    <Box width="100%" textAlign="center">
      {FailIcon}
      <Typography>
        Saving failed! <br />
        Something went wrong with updating data. Please go back and try again.
      </Typography>
    </Box>
  );

  // handle show result
  const showResult = (updateResult: ShowResultProps["updateResult"]) => {
    if (updateResult && isSuccessResponse(updateResult)) {
      const { docsFound, docsUpdated, entity } = updateResult?.data;

      return (
        <>
          {docsFound > 0 || docsUpdated > 0 ? (
            <Box width="100%" textAlign="center">
              {SuccessIcon}
              <Typography>
                Saving completed!
                <br />
                We found {docsFound || 0} {entity.toLowerCase()}
                {docsFound > 1 ? "s" : ""}, your changes have been updated to{" "}
                {docsUpdated} {`item${docsUpdated > 1 ? "s " : " "}`}{" "}
                successfully.
                {docsFound !== docsUpdated ? (
                  <>
                    <br />
                    {docsFound - docsUpdated} {entity.toLowerCase()}
                    {docsFound > 1 ? "s" : ""} already have the updated value.{" "}
                  </>
                ) : null}
              </Typography>
            </Box>
          ) : (
            <Box width="100%" textAlign="center">
              {FailIcon}
              <Typography>
                No items were found!
                <br />
                No items have been updated. Please double-check your filters and
                try again.
              </Typography>
            </Box>
          )}
        </>
      );
    } else {
      if (updateResult?.data.errors) {
        return (
          <Box width="100%" textAlign="center">
            {FailIcon}
            <Typography>
              Saving failed!{" "}
              {updateResult?.data.errors.detail
                ? `Reason: ${updateResult?.data.errors.detail}`
                : ""}
            </Typography>
          </Box>
        );
      } else {
        return <>{FailMsg}</>;
      }
    }
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
      width="100%"
      padding={4}
    >
      {isLoading ? (
        <Box width="100%" textAlign="center">
          <ProgressIndicator />
          <Typography>
            Updating data...
            <br />
            Please wait a moment
          </Typography>
        </Box>
      ) : (
        <Box width="100%" textAlign="center">
          {showResult(updateResult)}
        </Box>
      )}
    </Box>
  );
};

export default memo(ShowResult);
